import { useCallback, useEffect, useMemo, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { isSSR } from '@polestar/component-warehouse-react/helpers/supports';
export var ResizeCallbackTypes;
(function (ResizeCallbackTypes) {
    ResizeCallbackTypes[ResizeCallbackTypes["height"] = 0] = "height";
    ResizeCallbackTypes[ResizeCallbackTypes["width"] = 1] = "width";
    ResizeCallbackTypes[ResizeCallbackTypes["both"] = 2] = "both";
})(ResizeCallbackTypes || (ResizeCallbackTypes = {}));
/**
 * This is a drop-in replacement for useCallback when you use it as `<div ref={callback} />`
 * The only difference is that it will update the callback when the size of the element changes.
 * This causes the callback to be fired again, so you can get node dimension again.
 *
 * Make sure that the callbackRef you create doesn't change when the dimensions you keep in a state also change (putting the results from `clientWidth` in the deps list for example), otherwise you will get an endless loop.
 *
 * ```js
 * import { useResizeCallback, ResizeCallbackTypes } from '@polestar/component-warehouse-react';
 *
 * const MyComponent = () => {
 *  const callbackRef = useResizeCallback(
 *    (node) => {
 *      // Width has changed
 *      console.log(node.clientWidth);
 *    },
 *    [],
 *    ResizeCallbackTypes.width, // ResizeCallbackTypes.height, ResizeCallbackTypes.both
 *  );
 *
 *  return <div ref={callbackRef} />;
 * };
 * ```
 *
 * @storybook Hooks/useResizeCallback
 */
const useResizeCallback = (callback, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
deps, type = ResizeCallbackTypes.width) => {
    const sizeKeyRef = useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const wrappedCallback = useCallback(callback, deps);
    const observer = useMemo(() => 
    // @ts-ignore
    new ResizeObserver((entries) => {
        // prevents "ResizeObserver loop limit exceeded" error is thrown in Safari
        // see: https://stackoverflow.com/a/58701523
        window === null || window === void 0 ? void 0 : window.requestAnimationFrame(() => {
            if (!Array.isArray(entries) || !entries.length) {
                return;
            }
            for (const entry of entries) {
                if (!entry.target.isConnected) {
                    continue;
                }
                const { width, height } = (entry === null || entry === void 0 ? void 0 : entry.borderBoxSize)
                    ? Array.isArray(entry === null || entry === void 0 ? void 0 : entry.borderBoxSize) && (entry === null || entry === void 0 ? void 0 : entry.borderBoxSize.length) > 0
                        ? {
                            width: entry.borderBoxSize[0].inlineSize,
                            height: entry.borderBoxSize[0].blockSize,
                        }
                        : // @ts-ignore
                            { width: entry.borderBoxSize.inlineSize, height: entry.borderBoxSize.blockSize }
                    : {
                        width: entry.target.clientWidth,
                        height: entry.target.clientHeight,
                    };
                const key = type === ResizeCallbackTypes.height
                    ? height
                    : type === ResizeCallbackTypes.width
                        ? width
                        : `${height}x${width}`;
                if (key !== sizeKeyRef.current) {
                    wrappedCallback === null || wrappedCallback === void 0 ? void 0 : wrappedCallback(entry.target);
                    // @ts-ignore
                    sizeKeyRef.current = key;
                }
            }
        });
    }), [type, sizeKeyRef, wrappedCallback]);
    const callbackRef = useCallback((node) => {
        if (isSSR) {
            return;
        }
        if (node) {
            observer.disconnect();
            observer.observe(node);
        }
        else {
            observer.disconnect();
        }
    }, [observer]);
    return callbackRef;
};
/**
 * This is a drop-in replacement for useCallback when you know which parent HTMLElement to listen for size changes.
 * The only difference is that it will update the callback when the size of the element changes.
 * This causes the callback to be fired again, so you can get node dimension again.
 *
 * Make sure that the callbackRef you create doesn't change when the dimensions you keep in a state also change (putting the results from `clientWidth` in the deps list for example), otherwise you will get an endless loop.
 *
 * ```js
 * import { useResizeParentCallback, ResizeCallbackTypes } from '@polestar/component-warehouse-react';
 *
 * const MyComponent = () => {
 *    parentNode,
 *    useResizeParentCallback(
 *      (node) => {
 *        // Width has changed
 *        console.log(node.clientWidth);
 *      },
 *    [],
 *    ResizeCallbackTypes.width, // ResizeCallbackTypes.height, ResizeCallbackTypes.both
 *  );
 *
 *  return <div ref={callbackRef} />;
 * };
 * ```
 *
 * @storybook Hooks/useResizeParentCallback
 */
export const useResizeParentCallback = (observeNode, callback, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
deps, type = ResizeCallbackTypes.width) => {
    const sizeKeyRef = useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const wrappedCallback = useCallback(callback, deps);
    const observer = useMemo(() => 
    // @ts-ignore
    new ResizeObserver((entries) => {
        // prevents "ResizeObserver loop limit exceeded" error is thrown in Safari
        // see: https://stackoverflow.com/a/58701523
        window === null || window === void 0 ? void 0 : window.requestAnimationFrame(() => {
            if (!Array.isArray(entries) || !entries.length) {
                return;
            }
            for (const entry of entries) {
                if (!entry.target.isConnected) {
                    continue;
                }
                const { width, height } = (entry === null || entry === void 0 ? void 0 : entry.borderBoxSize)
                    ? Array.isArray(entry === null || entry === void 0 ? void 0 : entry.borderBoxSize) && (entry === null || entry === void 0 ? void 0 : entry.borderBoxSize.length) > 0
                        ? {
                            width: entry.borderBoxSize[0].inlineSize,
                            height: entry.borderBoxSize[0].blockSize,
                        }
                        : // @ts-ignore
                            { width: entry.borderBoxSize.inlineSize, height: entry.borderBoxSize.blockSize }
                    : {
                        width: entry.target.clientWidth,
                        height: entry.target.clientHeight,
                    };
                const key = type === ResizeCallbackTypes.height
                    ? height
                    : type === ResizeCallbackTypes.width
                        ? width
                        : `${height}x${width}`;
                if (key !== sizeKeyRef.current) {
                    wrappedCallback === null || wrappedCallback === void 0 ? void 0 : wrappedCallback(entry.target);
                    // @ts-ignore
                    sizeKeyRef.current = key;
                }
            }
        });
    }), [type, sizeKeyRef, wrappedCallback]);
    useEffect(() => {
        if (isSSR) {
            return;
        }
        if (observeNode) {
            observer.disconnect();
            observer.observe(observeNode);
        }
        else {
            observer.disconnect();
        }
    }, [observer, observeNode]);
};
export default useResizeCallback;
